import React, { useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Alert, AlertDescription } from '../ui/alert';
import { Shield, Smartphone, Download, CheckCircle, XCircle, Loader2, Copy, Check } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function TwoFactorSetup({ onComplete }) {
  const [step, setStep] = useState('intro'); // intro, setup, verify, complete
  const [setupData, setSetupData] = useState(null);
  const [verificationCode, setVerificationCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [copiedCode, setCopiedCode] = useState(null);

  const startSetup = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await axios.post(`${API_URL}/api/security/2fa/setup`);
      setSetupData(response.data);
      setStep('setup');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to setup 2FA');
    } finally {
      setLoading(false);
    }
  };

  const verifyAndEnable = async () => {
    try {
      setLoading(true);
      setError(null);
      
      await axios.post(`${API_URL}/api/security/2fa/enable`, {
        code: verificationCode
      });
      
      setStep('complete');
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid verification code');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text, index) => {
    navigator.clipboard.writeText(text);
    setCopiedCode(index);
    setTimeout(() => setCopiedCode(null), 2000);
  };

  const downloadBackupCodes = () => {
    const text = `MyMedMember 2FA Backup Codes\n\nGenerated: ${new Date().toLocaleString()}\n\nBackup Codes:\n${setupData.backup_codes.join('\n')}\n\nImportant:\n- Keep these codes in a safe place\n- Each code can only be used once\n- Use them if you lose access to your authenticator app`;
    
    const blob = new Blob([text], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'mymedmember-backup-codes.txt';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Intro Step
  if (step === 'intro') {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5 text-teal-600" />
            Enable Two-Factor Authentication
          </CardTitle>
          <CardDescription>
            Add an extra layer of security to your account
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-3">
            <div className="flex items-start gap-3">
              <div className="p-2 bg-teal-100 rounded-lg">
                <Shield className="h-5 w-5 text-teal-600" />
              </div>
              <div>
                <h4 className="font-semibold">Enhanced Security</h4>
                <p className="text-sm text-gray-600">
                  Protect your account even if your password is compromised
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Smartphone className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <h4 className="font-semibold">Mobile App Required</h4>
                <p className="text-sm text-gray-600">
                  Works with Google Authenticator, Authy, Microsoft Authenticator, or similar apps
                </p>
              </div>
            </div>
          </div>

          {error && (
            <Alert variant="destructive">
              <XCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <Button
            onClick={startSetup}
            disabled={loading}
            className="w-full bg-teal-600 hover:bg-teal-700"
          >
            {loading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Setting up...
              </>
            ) : (
              'Get Started'
            )}
          </Button>
        </CardContent>
      </Card>
    );
  }

  // Setup Step - Show QR Code
  if (step === 'setup') {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Scan QR Code</CardTitle>
          <CardDescription>
            Use your authenticator app to scan this QR code
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* QR Code */}
          <div className="flex flex-col items-center gap-4">
            <div className="p-4 bg-white border-2 border-gray-200 rounded-lg">
              <img
                src={setupData.qr_code}
                alt="2FA QR Code"
                className="w-64 h-64"
              />
            </div>

            {/* Manual Entry Option */}
            <div className="w-full p-4 bg-gray-50 rounded-lg">
              <p className="text-sm font-medium mb-2">Can't scan? Enter this code manually:</p>
              <div className="flex items-center gap-2">
                <code className="flex-1 p-2 bg-white border rounded text-sm break-all">
                  {setupData.secret}
                </code>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => copyToClipboard(setupData.secret, 'secret')}
                >
                  {copiedCode === 'secret' ? (
                    <Check className="h-4 w-4 text-green-600" />
                  ) : (
                    <Copy className="h-4 w-4" />
                  )}
                </Button>
              </div>
            </div>
          </div>

          {/* Backup Codes */}
          <div className="border-t pt-4">
            <div className="flex items-center justify-between mb-2">
              <h4 className="font-semibold">Backup Codes</h4>
              <Button
                size="sm"
                variant="outline"
                onClick={downloadBackupCodes}
              >
                <Download className="mr-2 h-4 w-4" />
                Download
              </Button>
            </div>
            <p className="text-sm text-gray-600 mb-3">
              Save these codes in a safe place. You can use them to access your account if you lose your phone.
            </p>
            <div className="grid grid-cols-2 gap-2 max-h-48 overflow-y-auto">
              {setupData.backup_codes.map((code, index) => (
                <div
                  key={index}
                  className="flex items-center gap-2 p-2 bg-gray-50 rounded border"
                >
                  <code className="flex-1 text-sm">{code}</code>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => copyToClipboard(code, index)}
                    className="h-6 w-6 p-0"
                  >
                    {copiedCode === index ? (
                      <Check className="h-3 w-3 text-green-600" />
                    ) : (
                      <Copy className="h-3 w-3" />
                    )}
                  </Button>
                </div>
              ))}
            </div>
          </div>

          <Button
            onClick={() => setStep('verify')}
            className="w-full bg-teal-600 hover:bg-teal-700"
          >
            Continue to Verification
          </Button>
        </CardContent>
      </Card>
    );
  }

  // Verify Step
  if (step === 'verify') {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Verify Setup</CardTitle>
          <CardDescription>
            Enter the 6-digit code from your authenticator app
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {error && (
            <Alert variant="destructive">
              <XCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <div>
            <Label htmlFor="code">Verification Code</Label>
            <Input
              id="code"
              type="text"
              placeholder="000000"
              maxLength={6}
              value={verificationCode}
              onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, ''))}
              className="text-center text-2xl tracking-widest"
              autoFocus
            />
            <p className="text-xs text-gray-500 mt-1">
              Enter the 6-digit code shown in your authenticator app
            </p>
          </div>

          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => setStep('setup')}
              className="flex-1"
            >
              Back
            </Button>
            <Button
              onClick={verifyAndEnable}
              disabled={verificationCode.length !== 6 || loading}
              className="flex-1 bg-teal-600 hover:bg-teal-700"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Verifying...
                </>
              ) : (
                'Verify & Enable'
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Complete Step
  if (step === 'complete') {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-green-600">
            <CheckCircle className="h-6 w-6" />
            2FA Enabled Successfully!
          </CardTitle>
          <CardDescription>
            Your account is now protected with two-factor authentication
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
            <h4 className="font-semibold text-green-900 mb-2">What's Next?</h4>
            <ul className="space-y-1 text-sm text-green-800">
              <li>• You'll need your authenticator app to login from now on</li>
              <li>• Keep your backup codes in a safe place</li>
              <li>• You can disable 2FA anytime from security settings</li>
            </ul>
          </div>

          <Button
            onClick={onComplete}
            className="w-full bg-teal-600 hover:bg-teal-700"
          >
            Done
          </Button>
        </CardContent>
      </Card>
    );
  }
}
